import React, { useEffect, useState } from 'react';
import { Box, Typography, Button, CircularProgress } from '@mui/material';
import { Save } from 'lucide-react';
import { useAppDispatch } from 'store/hooks';
import { showLoader, hideLoader } from 'store/uiSlice';
import {
  siteWheelService,
  SiteWheelSettings,
  SiteWheelDailyConfig,
} from 'components/api/siteWheel';
import { Alert } from 'components/alert/alert';
import styles from './WheelPrizeSettings.module.scss'; // Reuse common styles if needed, or create new ones

interface Props {
  siteId: string | number;
}

const WheelGeneralSettings: React.FC<Props> = ({ siteId }) => {
  const dispatch = useAppDispatch();

  const [loading, setLoading] = useState(true);
  const [statusAlert, setStatusAlert] = useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);

  // Daily Config State
  const [dailyConfig, setDailyConfig] = useState<Partial<SiteWheelDailyConfig>>({
    minDepositAmount: '',
    lookbackDays: '',
  });
  const [hasDailyChanges, setHasDailyChanges] = useState(false);

  // General Settings State
  const [generalConfig, setGeneralConfig] = useState<Partial<SiteWheelSettings>>({
    maxWinPerSpin: null,
    wageringMultiplier: '1',
    depositMatchExpiryDays: 30,
    freeSpinValidDays: 7,
  });
  const [hasGeneralChanges, setHasGeneralChanges] = useState(false);

  useEffect(() => {
    if (statusAlert) {
      const timer = setTimeout(() => setStatusAlert(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [statusAlert]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [dailyRes, generalRes] = await Promise.all([
        siteWheelService.getDailyConfig(siteId),
        siteWheelService.getSettings(siteId),
      ]);

      const dailyData = dailyRes.data || dailyRes;
      setDailyConfig({
        minDepositAmount: dailyData.minDepositAmount || '',
        lookbackDays: dailyData.lookbackDays || '',
      });

      const genData = generalRes.data || {};
      setGeneralConfig({
        maxWinPerSpin: genData.maxWinPerSpin || null,
        wageringMultiplier: genData.wageringMultiplier || '1',
        depositMatchExpiryDays: genData.depositMatchExpiryDays || 30,
        freeSpinValidDays: genData.freeSpinValidDays || 7,
      });

      setHasDailyChanges(false);
      setHasGeneralChanges(false);
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        'Failed to fetch settings';
      setStatusAlert({ message: errorMessage, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [siteId]);

  const handleSaveDaily = async () => {
    dispatch(showLoader({ text: 'Saving daily config...' }));
    try {
      const payload = {
        minDepositAmount:
          dailyConfig.minDepositAmount !== '' ? Number(dailyConfig.minDepositAmount) : 0,
        lookbackDays: dailyConfig.lookbackDays !== '' ? Number(dailyConfig.lookbackDays) : 0,
      };
      await siteWheelService.updateDailyConfig(siteId, payload);
      setHasDailyChanges(false);
      setStatusAlert({ message: 'Daily config saved successfully', type: 'success' });
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        'Failed to save daily config';
      setStatusAlert({ message: errorMessage, type: 'error' });
    } finally {
      dispatch(hideLoader());
    }
  };

  const handleSaveGeneral = async () => {
    dispatch(showLoader({ text: 'Saving general settings...' }));
    try {
      const payload = {
        ...generalConfig,
        maxWinPerSpin: generalConfig.maxWinPerSpin ? Number(generalConfig.maxWinPerSpin) : null,
        wageringMultiplier: generalConfig.wageringMultiplier
          ? Number(generalConfig.wageringMultiplier)
          : 1,
      };
      await siteWheelService.updateSettings(siteId, payload);
      setHasGeneralChanges(false);
      setStatusAlert({ message: 'General settings saved successfully', type: 'success' });
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        'Failed to save general settings';
      setStatusAlert({ message: errorMessage, type: 'error' });
    } finally {
      dispatch(hideLoader());
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress sx={{ color: 'var(--accent-blue)' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {statusAlert && <Alert message={statusAlert.message} type={statusAlert.type} />}

      {/* Daily Config Section */}
      <Box
        className={styles.tableWrapper}
        sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 3 }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ color: 'var(--text-primary)', fontWeight: 600 }}>
            Daily Config
          </Typography>
          <Button
            variant="contained"
            disabled={!hasDailyChanges}
            startIcon={<Save size={18} />}
            onClick={handleSaveDaily}
            className={styles.saveBtn}
          >
            Save
          </Button>
        </Box>

        <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>
              Min Deposit Amount
            </Typography>
            <input
              className={styles.inputField}
              value={dailyConfig.minDepositAmount}
              onChange={(e) => {
                setDailyConfig({ ...dailyConfig, minDepositAmount: e.target.value });
                setHasDailyChanges(true);
              }}
              style={{ width: '200px' }}
            />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>
              Lookback Days
            </Typography>
            <input
              className={styles.inputField}
              value={dailyConfig.lookbackDays}
              onChange={(e) => {
                setDailyConfig({ ...dailyConfig, lookbackDays: e.target.value });
                setHasDailyChanges(true);
              }}
              style={{ width: '200px' }}
            />
          </Box>
        </Box>
      </Box>

      {/* General Settings Section */}
      <Box
        className={styles.tableWrapper}
        sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 3 }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ color: 'var(--text-primary)', fontWeight: 600 }}>
            General Settings
          </Typography>
          <Button
            variant="contained"
            disabled={!hasGeneralChanges}
            startIcon={<Save size={18} />}
            onClick={handleSaveGeneral}
            className={styles.saveBtn}
          >
            Save
          </Button>
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: 4,
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>
              Max Win Per Spin
            </Typography>
            <input
              className={styles.inputField}
              value={generalConfig.maxWinPerSpin || ''}
              placeholder="Unlimited"
              onChange={(e) => {
                setGeneralConfig({ ...generalConfig, maxWinPerSpin: e.target.value || null });
                setHasGeneralChanges(true);
              }}
              style={{ width: '100%' }}
            />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>
              Wagering Multiplier
            </Typography>
            <input
              className={styles.inputField}
              value={generalConfig.wageringMultiplier || ''}
              onChange={(e) => {
                setGeneralConfig({ ...generalConfig, wageringMultiplier: e.target.value });
                setHasGeneralChanges(true);
              }}
              style={{ width: '100%' }}
            />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>
              Deposit Match Expiry (Days)
            </Typography>
            <input
              className={styles.inputField}
              value={generalConfig.depositMatchExpiryDays || ''}
              onChange={(e) => {
                setGeneralConfig({
                  ...generalConfig,
                  depositMatchExpiryDays: Number(e.target.value),
                });
                setHasGeneralChanges(true);
              }}
              style={{ width: '100%' }}
            />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>
              Free Spin Valid (Days)
            </Typography>
            <input
              className={styles.inputField}
              value={generalConfig.freeSpinValidDays || ''}
              onChange={(e) => {
                setGeneralConfig({ ...generalConfig, freeSpinValidDays: Number(e.target.value) });
                setHasGeneralChanges(true);
              }}
              style={{ width: '100%' }}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default WheelGeneralSettings;
